import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

/**
 * Returns `true` only when the page has fully loaded AND the Next.js router
 * is not mid-transition.  Resets to `false` on every `routeChangeStart` and
 * flips back to `true` on `routeChangeComplete` (or `routeChangeError`).
 * 
 * Reference - https://developer.mozilla.org/en-US/docs/Web/API/Document/readyState
 *
 * Use this to defer side-effects (e.g. opening modals) that should wait
 * until the destination page is stable.
 */
export function useRouteSettled(): boolean {
  const router = useRouter();
  const [settled, setSettled] = useState(() => typeof document !== 'undefined' && document.readyState === 'complete');

  useEffect(() => {
    const markSettled = () => setSettled(true);
    const markUnsettled = () => setSettled(false);

    if (document.readyState === 'complete') {
      setSettled(true);
    } else {
      window.addEventListener('load', markSettled);
    }

    router.events.on('routeChangeStart', markUnsettled);
    router.events.on('routeChangeComplete', markSettled);
    router.events.on('routeChangeError', markSettled);

    return () => {
      window.removeEventListener('load', markSettled);
      router.events.off('routeChangeStart', markUnsettled);
      router.events.off('routeChangeComplete', markSettled);
      router.events.off('routeChangeError', markSettled);
    };
  }, [router.events]);

  return settled;
}
